import type { ComponentType } from 'react';
import { createSerialTaskCache } from './createSerialTaskCache';

export type RenderPreviewStillInput<P extends Record<string, unknown>> = {
  component: ComponentType<P>;
  inputProps: P;
  width: number;
  height: number;
  fps: number;
  durationInFrames: number;
  frame: number;
  signal?: AbortSignal;
  /** Output image format. Defaults to 'jpeg' (quality 0.92). */
  format?: 'jpeg' | 'png';
  /** Remotion composition id passed to the web renderer. Defaults to 'cover-preview-still'. */
  compositionId?: string;
};

// The renderer's own per-asset budget fires first and names the stuck asset
// (e.g. `Loading <Img> with src=…`). The deadline is only a backstop for a
// render that never settles, and it starts when THIS render starts: renders
// are queued one at a time (the renderer allows no more), so time spent
// waiting behind another render no longer counts against it.
const DELAY_RENDER_TIMEOUT_MS = 20_000;
const STILL_DEADLINE_MS = 60_000;

const stills = createSerialTaskCache<string>();

/** Short stable fingerprint of a still request (FNV-1a over its JSON). */
function fingerprint(input: RenderPreviewStillInput<Record<string, unknown>>): string {
  const json = JSON.stringify(
    [
      input.inputProps,
      input.width,
      input.height,
      input.fps,
      input.durationInFrames,
      Math.round(input.frame),
      input.format ?? 'jpeg',
      input.compositionId ?? 'cover-preview-still',
    ],
    (_key, value) => (typeof value === 'function' ? undefined : value),
  );
  let hash = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    hash ^= json.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${(hash >>> 0).toString(36)}:${json.length}`;
}

/**
 * Renders one frame of a Remotion composition in the browser
 * (`@remotion/web-renderer`) and returns it as a JPEG data URL. The renderer
 * is loaded on demand, so it costs nothing until the user asks for a
 * preview frame. Identical requests (the "Usar frame atual" chooser and the
 * AI dialog at the same playhead) share one render.
 */
export function renderPreviewStillDataUrl<P extends Record<string, unknown>>(
  input: RenderPreviewStillInput<P>,
): Promise<string> {
  return stills.run(
    fingerprint(input as RenderPreviewStillInput<Record<string, unknown>>),
    () => renderOnce(input),
  );
}

async function renderOnce<P extends Record<string, unknown>>({
  component,
  inputProps,
  width,
  height,
  fps,
  durationInFrames,
  frame,
  signal,
  format = 'jpeg',
  compositionId = 'cover-preview-still',
}: RenderPreviewStillInput<P>): Promise<string> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  let deadline: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    deadline = setTimeout(() => {
      controller.abort();
      reject(new Error(`preview still did not finish within ${STILL_DEADLINE_MS / 1000}s`));
    }, STILL_DEADLINE_MS);
  });

  const render = async (): Promise<string> => {
    const { renderStillOnWeb } = await import('@remotion/web-renderer');
    const result = await renderStillOnWeb({
      composition: {
        component: component as ComponentType<Record<string, unknown>>,
        id: compositionId,
        width,
        height,
        fps,
        durationInFrames,
      },
      inputProps,
      frame: Math.max(0, Math.min(durationInFrames - 1, Math.round(frame))),
      delayRenderTimeoutInMilliseconds: DELAY_RENDER_TIMEOUT_MS,
      signal: controller.signal,
      licenseKey: import.meta.env.VITE_REMOTION_LICENSE_KEY || null,
      logLevel: 'error',
    });
    const blob = await result.blob(format === 'png' ? { format: 'png' } : { format: 'jpeg', quality: 0.92 });
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('still read failed'));
      reader.readAsDataURL(blob);
    });
  };

  try {
    return await Promise.race([render(), timedOut]);
  } finally {
    clearTimeout(deadline);
    signal?.removeEventListener('abort', abort);
  }
}
